import { useCallback, useEffect, useState } from 'react';

import { useIntl } from 'react-intl';

import { IOffersFragment } from 'generated/graphql-gateway';
import { useLoyaltyEngineRewards } from 'hooks/use-loyalty-engine-rewards';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import {
  IncentiveEvaluationErrorCodes,
  IncentiveEvaluationMap,
  LoyaltyOfferWithInlineAlertMessage,
} from 'state/loyalty/hooks/types';
import {
  compareBy_idAndErrors,
  createListComparator,
  useConditionalState,
} from 'state/loyalty/hooks/use-conditional-state';
import {
  getWalmartOfferUnavailableMessage,
  getWalmartSeasonalConfigOfferIds,
  isWalmartOffer,
} from 'state/loyalty/hooks/utils/walmart';
import { useInRestaurantRedemptionContext } from 'state/loyalty/in-restaurant-redemption';
import { LoyaltyOffer, LoyaltyOfferGroup } from 'state/loyalty/types';
import { useOrderContext } from 'state/order';

import { useLoyaltyIncentivesAvailabilityUtils } from '../loyalty-incentives-components/use-loyalty-incentives-availability';
import { useLoyaltyWalmart } from '../loyalty-partners/walmart/use-loyalty-walmart';

import { useLoyaltyOffersFilters } from './use-loyalty-offers-filters';
import { useWalmartSeasonalPromoAvailabilityUtils } from './use-walmart-seasonal-promo-availability-utils';
import { incentiveHasEvaluationError, reduceOffers } from './utils';

interface UseReduceOffersProps {
  cmsOffers: LoyaltyOffer[];
  cmsOfferGroups: LoyaltyOfferGroup[];
  personalizedOffers: LoyaltyOffer[];
  baseOffers: IOffersFragment[];
  offersLoading: boolean;
  refreshOffersLoading?: boolean;
}

const compareBy_idAndOffers = createListComparator(['_id', 'offers']);

export function useReduceOffers({
  cmsOffers,
  cmsOfferGroups,
  personalizedOffers,
  baseOffers,
  offersLoading,
  refreshOffersLoading = false,
}: UseReduceOffersProps) {
  const formatters = useIntl();

  const { data: loyaltyWalmart } = useLoyaltyWalmart();
  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const { inRestaurantRedemptionCart } = useInRestaurantRedemptionContext();
  const {
    checkLoyaltyOfferAvailability,
    getIncentiveUnavailableMessage,
  } = useLoyaltyIncentivesAvailabilityUtils();
  const walmartUtils = useWalmartSeasonalPromoAvailabilityUtils(
    loyaltyWalmart?.seasonalPromoConfig?.configOffers || undefined
  );
  const { engineRewardsMap } = useLoyaltyEngineRewards();

  const enableSortOffersForServiceMode = useFlag(
    LaunchDarklyFlag.ENABLE_SORT_OFFERS_FOR_SERVICE_MODE
  );
  const { appliedFilters } = useLoyaltyOffersFilters();
  const dispatch = useAppDispatch();

  // @TODO: We could think about default to null values instead of emtpty arrays here
  const [offers, setOffers] = useConditionalState(
    [] as LoyaltyOfferWithInlineAlertMessage[],
    compareBy_idAndErrors
  );
  const [offersInCart, setOffersInCart] = useConditionalState(
    [] as LoyaltyOffer[],
    compareBy_idAndErrors
  );
  const [offerGroups, setOfferGroups] = useConditionalState(
    [] as LoyaltyOfferGroup[],
    compareBy_idAndOffers
  );

  const [reductionLoading, setReductionLoading] = useState<boolean>(true);

  const { serviceMode } = useOrderContext();

  const runOffersReduction = useCallback(() => {
    if (!cmsOffers?.length || offersLoading || refreshOffersLoading) {
      return;
    }

    setReductionLoading(true);

    try {
      const [_offers, _offersInCart, _offerGroups] = reduceOffers({
        cmsOffers,
        cmsOfferGroups,
        personalizedOffers,
        baseOffers,
        appliedOffers,
        inRestaurantRedemptionCart,
        checkLoyaltyOfferAvailability,
        getIncentiveUnavailableMessage: (incentive: LoyaltyOffer) => {
          const loyaltyEngineId = incentive.loyaltyEngineId;
          if (!loyaltyEngineId) {
            return;
          }

          // Override Walmart seasonal promo max redemption availability error with custom message
          if (getWalmartSeasonalConfigOfferIds(loyaltyWalmart).includes(incentive._id)) {
            const message = walmartUtils.getIncentiveUnavailableMessage(incentive);

            if (message) {
              return message;
            }
          }

          // Override Walmart offer max redemption availability error with custom message
          if (isWalmartOffer(incentive)) {
            if (
              incentiveHasEvaluationError(
                incentive,
                IncentiveEvaluationErrorCodes.EXCEEDS_MAX_REDEMPTION
              )
            ) {
              return getWalmartOfferUnavailableMessage(incentive._id, formatters);
            }
          }

          return getIncentiveUnavailableMessage({
            incentive,
            pointCost: engineRewardsMap[loyaltyEngineId]?.pointCost,
            locked: engineRewardsMap[loyaltyEngineId]?.locked,
          });
        },
        enableSortOffersForServiceMode,
        // @ts-expect-error TS(2322) FIXME: Type '(offerFeedbackMap: IncentiveEvaluationMap) =... Remove this comment to see the full error message
        setOffersFeedbackMap: (offerFeedbackMap: IncentiveEvaluationMap) =>
          dispatch(actions.loyalty.setOffersFeedbackMap(offerFeedbackMap)),
        appliedFilters,
        selectedServiceMode: serviceMode,
        formatters,
      });

      setOffers(_offers);
      setOffersInCart(_offersInCart);
      setOfferGroups(_offerGroups);
    } catch (e) {
      // If the offers reduce fails, we ensure the user will not see and endless loading screen,
      // and instead sees the offers empty screens
      setReductionLoading(false);
    } finally {
      setReductionLoading(false);
    }
  }, [
    cmsOffers,
    offersLoading,
    refreshOffersLoading,
    cmsOfferGroups,
    personalizedOffers,
    baseOffers,
    appliedOffers,
    inRestaurantRedemptionCart,
    checkLoyaltyOfferAvailability,
    enableSortOffersForServiceMode,
    appliedFilters,
    serviceMode,
    formatters,
    setOffers,
    setOffersInCart,
    setOfferGroups,
    getIncentiveUnavailableMessage,
    engineRewardsMap,
    dispatch,
    loyaltyWalmart,
    walmartUtils,
  ]);

  // @TODO: We should review this, how we trigger the offers reduction
  useEffect(() => {
    runOffersReduction();
  }, [runOffersReduction]);

  return {
    offers,
    offersInCart,
    offerGroups,
    reductionLoading,
    reprocessOffers: runOffersReduction,
  };
}
